import type { Edge, Node } from '@xyflow/react';
import {
  MAIL_EDITOR_MAX_DELAY_BLOCKS,
  MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS,
  countSmsViberDelayBlocks,
  normalizeChannelNodeData,
  visualChainIsEventTrigger,
  type MailingCampaign,
} from './mailingTypes';
import i18next from '../../i18n/initI18n';
import { linearizeVisualChannelSteps } from './visualChainLinearize';
import { visualSmsViberDelayBetweenChannelsReady } from './visualFlowReadiness';
import { hasDuplicateOutgoingBySourceHandle } from './flowConnectionRules';

function forwardReach(startId: string, edges: Edge[], allIds: Set<string>): Set<string> {
  const seen = new Set<string>();
  const q = [startId];
  while (q.length) {
    const id = q.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of edges) {
      if (e.source === id && allIds.has(e.target)) q.push(e.target);
    }
  }
  return seen;
}

function outgoing(edges: Edge[], fromId: string, sourceHandle: string | null | undefined): Edge[] {
  return edges.filter((e) => {
    if (e.source !== fromId) return false;
    const sh = e.sourceHandle ?? null;
    const want = sourceHandle ?? null;
    if (want === null || want === '') {
      return sh === null || sh === undefined || sh === '' || String(sh) === 'out';
    }
    return sh === want;
  });
}

export type FlowValidationResult = { ok: boolean; errors: string[] };

/** Заметки не требуют пути от старта до конца. */
export function nodeExcludedFromReachability(type: string | undefined): boolean {
  return type === 'mailingNote';
}

export function validateMailingFlow(campaign: MailingCampaign): FlowValidationResult {
  const errors: string[] = [];
  const { nodes, edges } = campaign;
  if (hasDuplicateOutgoingBySourceHandle(edges)) {
    errors.push(i18next.t('validation.duplicateOutgoing'));
  }
  const limits = countSmsViberDelayBlocks(nodes);
  const isEvent = visualChainIsEventTrigger(nodes);
  if (!isEvent) {
    if (limits.sms > 1) errors.push(i18next.t('validation.maxOneSmsBlock'));
    if (limits.viber > 1) errors.push(i18next.t('validation.maxOneViberBlock'));
    if (limits.email > 1) errors.push(i18next.t('validation.maxOneEmailBlock'));
  } else {
    const max = MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS;
    if (limits.sms > max) errors.push(i18next.t('validation.maxEventChannelBlocks', { channel: 'SMS', max }));
    if (limits.viber > max) errors.push(i18next.t('validation.maxEventChannelBlocks', { channel: 'Viber', max }));
    if (limits.email > max) errors.push(i18next.t('validation.maxEventChannelBlocks', { channel: 'Email', max }));
    const start = nodes.find((n) => n.type === 'mailingStart');
    const sd = start?.data as { eventFieldKey?: string; scheduleTime?: string } | undefined;
    if (!String(sd?.eventFieldKey ?? '').trim()) {
      errors.push(i18next.t('validation.eventFieldRequired'));
    }
    if (!String(sd?.scheduleTime ?? '').trim()) {
      errors.push(i18next.t('validation.eventSendTimeRequired'));
    }
    const offsets: number[] = [];
    for (const n of nodes) {
      if (n.type !== 'mailingChannel') continue;
      const d = normalizeChannelNodeData(n.data);
      if (d.channel !== 'sms' && d.channel !== 'viber' && d.channel !== 'email') continue;
      const raw = String(d.triggerOffsetDays ?? '').trim();
      if (!raw) {
        errors.push(i18next.t('validation.triggerOffsetRequired'));
        continue;
      }
      const off = Number(raw);
      if (!Number.isFinite(off) || off < 0) {
        errors.push(i18next.t('validation.triggerOffsetInvalid'));
        continue;
      }
      offsets.push(off);
    }
    if (offsets.length === 0) {
      errors.push(i18next.t('validation.eventChannelStepRequired'));
    }
  }
  if (limits.delay > MAIL_EDITOR_MAX_DELAY_BLOCKS) {
    errors.push(i18next.t('validation.maxDelayBlocksExceeded', { max: MAIL_EDITOR_MAX_DELAY_BLOCKS }));
  }
  const start = nodes.find((n) => n.type === 'mailingStart');
  const endNodes = nodes.filter((n) => n.type === 'mailingEnd');
  if (!start) errors.push(i18next.t('validation.needStart'));
  if (endNodes.length === 0) errors.push(i18next.t('validation.needEnd'));

  const allIds = new Set(nodes.map((n) => n.id));
  if (start && endNodes.length) {
    const reach = forwardReach(start.id, edges, allIds);
    const anyEnd = endNodes.some((e) => reach.has(e.id));
    if (!anyEnd) errors.push(i18next.t('validation.noPathFromStart'));

    for (const n of nodes) {
      if (n.id === start.id || n.type === 'mailingEnd') continue;
      if (nodeExcludedFromReachability(n.type)) continue;
      if (!reach.has(n.id)) {
        errors.push(
          i18next.t('validation.unreachable', {
            title: (n.data as { title?: string })?.title || n.id,
            type: String(n.type),
          })
        );
      }
    }
  }

  for (const n of nodes) {
    if (n.type === 'mailingCondition') {
      if (outgoing(edges, n.id, 'yes').length === 0) {
        errors.push(i18next.t('validation.conditionYes'));
      }
      if (outgoing(edges, n.id, 'no').length === 0) {
        errors.push(i18next.t('validation.conditionNo'));
      }
    }
    if (n.type === 'mailingAbTest') {
      if (outgoing(edges, n.id, 'a').length === 0) errors.push(i18next.t('validation.abBranchA'));
      if (outgoing(edges, n.id, 'b').length === 0) errors.push(i18next.t('validation.abBranchB'));
    }
    if (n.type === 'mailingDelay') {
      const legacyOut = outgoing(edges, n.id, null).length > 0;
      const legacyDualBranches =
        outgoing(edges, n.id, 'delivered').length > 0 &&
        outgoing(edges, n.id, 'not_delivered').length > 0;
      if (!legacyOut && !legacyDualBranches) {
        errors.push(i18next.t('validation.delayOut'));
      }
    }
    if (n.type === 'mailingChannel') {
      const incoming = edges.filter((e) => e.target === n.id);
      if (incoming.length === 0) errors.push(i18next.t('validation.channelNoIncoming', { id: n.id }));
    }
  }

  const chsOrdered = linearizeVisualChannelSteps(nodes, edges).map((s) => s.channel);
  if (!isEvent && !visualSmsViberDelayBetweenChannelsReady(nodes, edges, chsOrdered)) {
    errors.push(i18next.t('domain.cascadeNeedsDelayBetweenChannels'));
  }

  return { ok: errors.length === 0, errors };
}
